import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FinanceClient, FinanceSeriePoint, getFinanceOverview, getFinanceEntries,
  addFinanceEntry, deleteFinanceEntry, activatePendingArena, postBillingAction,
  setFinanceConfig, setImplantacaoPrevista,
} from '../../api/gestor';
import { Icon, Modal, useToast } from './ui';

const fmt = (n: number) =>
  (n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

function monthShort(m: string) {
  const [y, mm] = m.split('-');
  const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${nomes[Number(mm) - 1]}/${y.slice(2)}`;
}

/* ---------- gráfico de barras receita x saídas + linha do acumulado ---------- */
function FinanceChart({ serie, focusMonth, onFocus }: {
  serie: FinanceSeriePoint[];
  focusMonth: string | null;
  onFocus: (m: string) => void;
}) {
  const W = 940; const H = 260; const PAD = 36; const BOTTOM = 36;
  const maxBar = Math.max(...serie.map(p => Math.max(p.receita, p.saidas)), 1);
  const accVals = serie.map(p => p.acumulado);
  const accMin = Math.min(...accVals, 0); const accMax = Math.max(...accVals, 1);
  const n = serie.length;
  const slot = (W - PAD * 2) / n;
  const barW = Math.min(22, slot / 3);
  const yBar = (v: number) => (H - BOTTOM) - (v / maxBar) * (H - BOTTOM - 20);
  const yAcc = (v: number) =>
    (H - BOTTOM) - ((v - accMin) / (accMax - accMin || 1)) * (H - BOTTOM - 20);
  const accPath = serie.map((p, i) =>
    `${i === 0 ? 'M' : 'L'}${PAD + slot * i + slot / 2},${yAcc(p.acumulado)}`).join(' ');
  const zeroY = yAcc(0);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
      <line x1={PAD} x2={W - PAD} y1={H - BOTTOM} y2={H - BOTTOM}
        stroke="rgba(255,255,255,0.18)" />
      {accMin < 0 && (
        <line x1={PAD} x2={W - PAD} y1={zeroY} y2={zeroY}
          stroke="rgba(255,255,255,0.25)" strokeDasharray="4 5" />
      )}
      {serie.map((p, i) => {
        const x = PAD + slot * i + slot / 2;
        const proj = p.tipo === 'projetado';
        const focused = focusMonth === p.month;
        return (
          <g key={p.month} style={{ cursor: 'pointer' }} onClick={() => onFocus(p.month)}
            opacity={focusMonth && !focused ? 0.45 : 1}>
            <rect x={x - barW - 2} width={barW} y={yBar(p.receita)}
              height={(H - BOTTOM) - yBar(p.receita)} rx={3}
              fill={proj ? 'rgba(126,226,63,0.35)' : 'var(--ft-green)'} />
            <rect x={x + 2} width={barW} y={yBar(p.saidas)}
              height={(H - BOTTOM) - yBar(p.saidas)} rx={3}
              fill={proj ? 'rgba(255,77,77,0.35)' : 'var(--bad)'} />
            <text x={x} y={H - BOTTOM + 16} textAnchor="middle"
              fill={focused ? '#fff' : 'var(--ap-muted)'}
              fontSize={11} fontFamily="JetBrains Mono, monospace">
              {monthShort(p.month)}{proj ? '*' : ''}
            </text>
            {focused && (
              <text x={x} y={14} textAnchor="middle" fill="#fff" fontSize={12} fontWeight={700}>
                {fmt(p.receita)} − {fmt(p.saidas)} = {fmt(p.saldo)}
              </text>
            )}
          </g>
        );
      })}
      <path d={accPath} fill="none" stroke="var(--ft-blue)" strokeWidth={2.5} />
      {serie.map((p, i) => (
        <circle key={p.month} cx={PAD + slot * i + slot / 2} cy={yAcc(p.acumulado)} r={3.5}
          fill={p.acumulado >= 0 ? 'var(--ft-green)' : 'var(--bad)'}
          stroke="var(--ap-bg)" strokeWidth={1.5} />
      ))}
    </svg>
  );
}

/* ---------- aba relatórios ---------- */
export function ReportsTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [focusMonth, setFocusMonth] = useState<string | null>(null);
  const [navMonth, setNavMonth] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [entryOpen, setEntryOpen] = useState(false);
  const [kind, setKind] = useState<'investimento' | 'despesa' | 'receita_manual' | 'retirada'>('investimento');
  const [sortKey, setSortKey] = useState<keyof FinanceClient>('lucro');
  const [sortDir, setSortDir] = useState<1 | -1>(-1);
  const [contaOpen, setContaOpen] = useState(false);
  const [contaNome, setContaNome] = useState('');
  const [cliente, setCliente] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [installments, setInstallments] = useState('1');
  const [startMonth, setStartMonth] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['finance'], queryFn: getFinanceOverview, staleTime: 60 * 1000,
  });
  const { data: entries } = useQuery({
    queryKey: ['finance-entries'], queryFn: getFinanceEntries, staleTime: 60 * 1000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['finance'] });
    queryClient.invalidateQueries({ queryKey: ['finance-entries'] });
  };
  const addMut = useMutation({
    mutationFn: addFinanceEntry,
    onSuccess: () => { invalidate(); toast('Lançamento salvo no banco'); setEntryOpen(false); },
    onError: (e: any) => toast(`Erro: ${e.message}`, 'warn'),
  });
  const delMut = useMutation({
    mutationFn: deleteFinanceEntry,
    onSuccess: () => { invalidate(); toast('Lançamento removido'); },
    onError: (e: any) => toast(`Erro: ${e.message}`, 'warn'),
  });
  const activateMut = useMutation({
    mutationFn: activatePendingArena,
    onSuccess: (r) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      toast(`${r.nome} ativada — entrou na planilha e no site`);
    },
    onError: (e: any) => toast(`Erro: ${e.message}`, 'warn'),
  });

  if (isLoading || !data) {
    return <div className="loading-page"><span className="spinner" /> calculando financeiro…</div>;
  }
  const g = data.global;
  const cliFocus = selected ? data.clientes.find(c => c.nome === selected) : null;
  const cliEntries = entries || [];
  const detailEntries = cliEntries.filter(e => e.cliente === selected);

  const sortBy = (key: keyof FinanceClient) => {
    if (sortKey === key) setSortDir(sortDir === 1 ? -1 : 1);
    else { setSortKey(key); setSortDir(-1); }
  };
  const arrow = (key: keyof FinanceClient) =>
    sortKey === key ? (sortDir === -1 ? ' ↓' : ' ↑') : '';
  const clientesOrdenados = [...data.clientes].sort((a, b) => {
    if (sortKey === 'payback_meses_restantes') {
      const av = a.ja_se_pagou ? 0 : (a.payback_meses_restantes ?? 999);
      const bv = b.ja_se_pagou ? 0 : (b.payback_meses_restantes ?? 999);
      return (av - bv) * sortDir;
    }
    const av = a[sortKey]; const bv = b[sortKey];
    if (typeof av === 'string' || typeof bv === 'string') {
      return String(av ?? '').localeCompare(String(bv ?? '')) * sortDir;
    }
    return ((Number(av) || 0) - (Number(bv) || 0)) * sortDir;
  });
  const totalCusto = (c: FinanceClient) => c.investido + c.despesas;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Relatórios</h1>
          <div className="sub">
            Financeiro do Apertai — dados do seu banco (Firestore) · meses com <b>*</b> são projeção
          </div>
        </div>
        <button className="btn btn-green" onClick={() => {
          setKind('investimento'); setCliente(selected || ''); setDescription('');
          setAmount(''); setInstallments('1'); setStartMonth(data.mes_atual); setEntryOpen(true);
        }}>
          <Icon name="plus" size={14} /> Lançar investimento / despesa
        </button>
      </div>

      <div className="kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className={`kpi ${(g.caixa_atual ?? 0) >= 0 ? 'good' : 'alert'}`}>
          <div className="klabel">
            <Icon name="money" size={12} /> Caixa atual · {g.conta_nome || 'Nubank'}
            <button className="iconbtn" style={{ width: 22, height: 22, marginLeft: 'auto' }}
              title="Renomear conta"
              onClick={() => { setContaNome(g.conta_nome || 'Nubank'); setContaOpen(true); }}>
              <Icon name="edit" size={11} />
            </button>
          </div>
          <div className="kval">{fmt(g.caixa_atual ?? 0)}</div>
          <div className="kfoot">
            entrou {fmt(g.recebido_total)} · retirado {fmt(g.retiradas_total ?? 0)} ·{' '}
            <button
              style={{ color: 'var(--warn)', cursor: 'pointer', fontWeight: 700,
                background: 'none', border: 0, padding: 0, font: 'inherit' }}
              onClick={() => {
                setKind('retirada'); setCliente(''); setDescription('');
                setAmount(''); setInstallments('1'); setStartMonth(data.mes_atual); setEntryOpen(true);
              }}>
              lançar retirada
            </button>
          </div>
        </div>
        <div className={`kpi ${g.lucro_total >= 0 ? 'good' : 'alert'}`}>
          <div className="klabel"><Icon name="money" size={12} /> Lucro (caixa)</div>
          <div className="kval">{fmt(g.lucro_total)}</div>
          <div className="kfoot">
            recebido {fmt(g.recebido_total)} · parcelas já pagas {fmt(g.saidas_vencidas ?? 0)}
          </div>
        </div>
        <div className="kpi">
          <div className="klabel"><Icon name="chip" size={12} /> Mensalidade prevista</div>
          <div className="kval">{fmt(g.mensalidade_prevista)}</div>
          <div className="kfoot">por mês, clientes ativos</div>
        </div>
        <div className={`kpi ${g.divida_futura > 0 ? 'maint' : ''}`}>
          <div className="klabel"><Icon name="doc" size={12} /> Parcelas a vencer</div>
          <div className="kval">{fmt(g.divida_futura)}</div>
          <div className="kfoot">dívida futura de implantações</div>
        </div>
        <div className="kpi">
          <div className="klabel"><Icon name="search" size={12} /> Break-even projetado</div>
          <div className="kval" style={{ fontSize: 28 }}>
            {g.break_even_projetado === 'ja positivo' ? 'positivo ✓' : g.break_even_projetado}
          </div>
          <div className="kfoot">saldo acumulado cruza zero</div>
        </div>
        <div className={`kpi ${(g.perda_implantacao ?? 0) > 0 ? 'alert' : 'maint'}`}>
          <div className="klabel"><Icon name="alert" size={12} /> Perdendo por atraso de implantação</div>
          <div className="kval">{fmt(g.perda_implantacao ?? 0)}</div>
          <div className="kfoot">
            <b>{(data.aguardando_implantacao || []).length}</b> quadras paradas ·{' '}
            {fmt((data.aguardando_implantacao || []).reduce((s, p) => s + p.investido, 0))} já gastos ·
            mensalidade × dias desde a data prevista
          </div>
        </div>
      </div>

      {(data.aguardando_implantacao || []).length > 0 && (
        <>
          <div className="sec-head">
            <h2><span className="underline" /> Aguardando implantação</h2>
            <div className="right">
              <span className="pill warn"><span className="k">sem cobrança</span> até ativar</span>
            </div>
          </div>
          {(data.aguardando_implantacao || []).map((p) => (
            <div key={p.id} className="srow">
              <span className="sicon"><Icon name="alert" /></span>
              <div>
                <div className="sname">
                  {p.nome}
                  {p.atraso_dias > 0 && (
                    <span className="deve-badge" title={`Prevista para ${p.prevista_em}`}>
                      {p.atraso_dias}d atrasada · perdendo {fmt(p.perda_estimada)}
                    </span>
                  )}
                </div>
                <div className="sdet">
                  {p.branch ? `branch ${p.branch} · ` : ''}investido até agora {fmt(p.investido)} ·
                  mensalidade prevista {fmt(p.valor_previsto)} · instalar em{' '}
                  <input type="date" defaultValue={p.prevista_em || ''}
                    style={{ background: 'var(--ap-bg-4)', border: '1px solid var(--ap-line)',
                      borderRadius: 6, color: 'var(--ap-text)', padding: '2px 6px',
                      font: 'inherit', fontSize: 11 }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={async (e) => {
                      const v = e.target.value;
                      if (!v) return;
                      try {
                        await setImplantacaoPrevista(p.nome, v);
                        invalidate();
                        toast(`${p.nome}: prevista para ${v}`);
                      } catch (err: any) { toast(`Erro: ${err.message}`, 'warn'); }
                    }} />
                </div>
              </div>
              <div className="sright" style={{ marginLeft: 'auto' }}>
                {p.id ? (
                  <button className="btn btn-green btn-sm" disabled={activateMut.isPending}
                    onClick={() => activateMut.mutate(p.id!)}>
                    {activateMut.isPending ? <span className="spinner" /> : <Icon name="check" size={13} />}
                    Ativar (entra na planilha)
                  </button>
                ) : (
                  <button className="btn btn-green btn-sm"
                    onClick={async () => {
                      try {
                        await postBillingAction((p as any).rowIndex, 'reabrir');
                        invalidate();
                        queryClient.invalidateQueries({ queryKey: ['billing'] });
                        toast(`${p.nome}: cobrança iniciada (PENDENTE na planilha)`);
                      } catch (e: any) { toast(`Erro: ${e.message}`, 'warn'); }
                    }}>
                    <Icon name="check" size={13} /> Iniciar cobrança
                  </button>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {(() => {
        const meses = Object.keys(data.meses_detalhe || {}).sort();
        const sel = navMonth && data.meses_detalhe[navMonth] ? navMonth : data.mes_atual;
        const d = data.meses_detalhe?.[sel];
        if (!d) return null;
        const idx = meses.indexOf(sel);
        const go = (delta: number) => {
          const ni = Math.max(0, Math.min(meses.length - 1, idx + delta));
          setNavMonth(meses[ni]);
        };
        const nomeMes = (m: string) => {
          const [y, mm] = m.split('-');
          const nomes = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
            'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
          return `${nomes[Number(mm) - 1]} ${y}`;
        };
        return (
          <>
            <div className="sec-head">
              <h2><span className="underline" /> Navegar por mês</h2>
              <div className="month-nav">
                <button className="iconbtn" disabled={idx <= 0} onClick={() => go(-1)}>‹</button>
                <span className="month-nav-label">
                  {nomeMes(sel)}
                  {d.tipo === 'projetado' && <span className="month-nav-tag">projeção</span>}
                  {sel === data.mes_atual && <span className="month-nav-tag now">atual</span>}
                </span>
                <button className="iconbtn" disabled={idx >= meses.length - 1} onClick={() => go(1)}>›</button>
              </div>
            </div>
            <div className="card card-pad">
              <div className="metric-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 18 }}>
                <div className="metric"><div className="ml">{d.tipo === 'projetado' ? 'Receita prevista' : 'Receita'}</div>
                  <div className="mv green">{fmt(d.total_receita)}</div></div>
                <div className="metric"><div className="ml">Gastos / parcelas</div>
                  <div className="mv" style={{ color: '#ff8a8a' }}>{fmt(d.total_saidas)}</div></div>
                <div className="metric"><div className="ml">Saldo do mês</div>
                  <div className="mv" style={{ color: d.saldo >= 0 ? 'var(--ft-green)' : 'var(--bad)' }}>{fmt(d.saldo)}</div></div>
              </div>
              <div className="month-cols">
                <div>
                  <h4 className="month-col-h">
                    {d.tipo === 'projetado' ? 'Recebimentos previstos' : 'Recebimentos'} · {fmt(d.total_receita)}
                  </h4>
                  {d.receita_itens.length === 0 && <div className="report-empty">Nada neste mês.</div>}
                  {d.receita_itens.map((r, i) => (
                    <div key={i} className="month-line">
                      <span className="dot ok" />
                      <span className="ml-nome">{r.cliente}</span>
                      {r.tipo === 'previsto' && <span className="ml-tag">previsto</span>}
                      <span className="ml-val green">{fmt(r.valor)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="month-col-h">Gastos e parcelas · {fmt(d.total_saidas)}</h4>
                  {d.saida_itens.length === 0 && <div className="report-empty">Nenhuma saída neste mês.</div>}
                  {d.saida_itens.map((s, i) => (
                    <div key={i} className="month-line">
                      <span className="dot bad" />
                      <span className="ml-nome">
                        {s.cliente !== '(geral)' ? `${s.cliente} · ` : ''}{s.descricao}
                        {s.parcela && <span className="ml-parc"> {s.parcela}</span>}
                      </span>
                      <span className="ml-val" style={{ color: '#ff8a8a' }}>{fmt(s.valor)}</span>
                    </div>
                  ))}
                  {d.retirada_itens.map((r, i) => (
                    <div key={`ret${i}`} className="month-line">
                      <span className="dot warn" />
                      <span className="ml-nome">retirada · {r.descricao}</span>
                      <span className="ml-val" style={{ color: 'var(--warn)' }}>{fmt(r.valor)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        );
      })()}

      <div className="sec-head">
        <h2><span className="underline" /> Fluxo mensal + projeção 6 meses</h2>
        <div className="right">
          <span className="pill"><span className="k">verde</span> receita</span>
          <span className="pill"><span className="k">vermelho</span> saídas</span>
          <span className="pill"><span className="k">linha azul</span> acumulado</span>
        </div>
      </div>
      <div className="card card-pad">
        <FinanceChart serie={data.serie} focusMonth={focusMonth}
          onFocus={(m) => { setFocusMonth(focusMonth === m ? null : m); setNavMonth(m); }} />
      </div>

      <div className="sec-head">
        <h2><span className="underline" /> Por quadra</h2>
        <div className="right">
          <span className="pill"><span className="k">clique</span> para detalhar e lançar</span>
        </div>
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="ptable ptable-lg">
            <thead>
              <tr>
                <th style={{ paddingLeft: 22, cursor: 'pointer' }} onClick={() => sortBy('nome')}>
                  Quadra / cliente{arrow('nome')}</th>
                <th className="r" style={{ cursor: 'pointer' }} onClick={() => sortBy('mensalidade')}>
                  Mensalidade{arrow('mensalidade')}</th>
                <th className="r" style={{ cursor: 'pointer' }} onClick={() => sortBy('recebido_total')}>
                  Recebido{arrow('recebido_total')}</th>
                <th className="r" style={{ cursor: 'pointer' }} onClick={() => sortBy('investido')}>
                  Investido + despesas{arrow('investido')}</th>
                <th className="r" style={{ cursor: 'pointer' }} onClick={() => sortBy('lucro')}>
                  Lucro{arrow('lucro')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => sortBy('payback_meses_restantes')}>
                  Payback{arrow('payback_meses_restantes')}</th>
              </tr>
            </thead>
            <tbody>
              {clientesOrdenados.map((c) => (
                <tr key={c.nome} style={{ cursor: 'pointer' }}
                  onClick={() => setSelected(c.nome)}>
                  <td className="cli" style={{ paddingLeft: 22 }}>
                    {c.nome}
                  </td>
                  <td className="r">{fmt(c.mensalidade)}</td>
                  <td className="r" style={{ color: 'var(--ft-green)', fontWeight: 700 }}>{fmt(c.recebido_total)}</td>
                  <td className="r" style={{ color: '#ff8a8a' }}>{fmt(totalCusto(c))}</td>
                  <td className={'r valor'} style={{ color: c.lucro >= 0 ? 'var(--ft-green)' : 'var(--bad)' }}>
                    {fmt(c.lucro)}
                  </td>
                  <td>
                    {c.investido <= 0 ? <span className="contato">sem custo lançado</span>
                      : c.ja_se_pagou ? <span className="tag-st pago"><span className="dot ok" />se pagou</span>
                        : c.payback_meses_restantes !== null
                          ? <span className="tag-st manut"><span className="dot warn" />~{c.payback_meses_restantes} meses</span>
                          : <span className="contato">sem mensalidade</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sec-head">
        <h2><span className="underline" /> Lançamentos (todos)</h2>
      </div>
      {cliEntries.length === 0 && (
        <div className="report-empty">
          Nenhum lançamento — comece lançando o custo inicial
          de implantação de cada quadra (ex: R$ 2.000 em 1x ou parcelado).
        </div>
      )}
      {cliEntries.map((e) => (
        <div key={e.id} className="srow">
          <span className="sicon">
            <Icon name={e.kind === 'investimento' ? 'chip' : e.kind === 'despesa' ? 'money' : e.kind === 'retirada' ? 'power' : 'check'} />
          </span>
          <div>
            <div className="sname">
              {e.kind === 'investimento' ? 'Investimento' : e.kind === 'despesa' ? 'Despesa' : e.kind === 'retirada' ? 'Retirada de caixa' : 'Receita manual'}
              {' · '}{e.cliente || 'Apertai (geral)'}{e.description ? ` — ${e.description}` : ''}
            </div>
            <div className="sdet">
              {fmt(e.amount)}{e.installments > 1 ? ` em ${e.installments}x de ${fmt(e.amount / e.installments)}` : ''} · a partir de {e.startMonth}
            </div>
          </div>
          <div className="sright" style={{ marginLeft: 'auto' }}>
            <button className="iconbtn" title="Remover lançamento"
              onClick={() => delMut.mutate(e.id)} disabled={delMut.isPending}>
              <Icon name="trash" />
            </button>
          </div>
        </div>
      ))}

      <Modal
        wide
        title={cliFocus ? cliFocus.nome : 'Quadra'}
        desc={cliFocus
          ? `Mensalidade ${fmt(cliFocus.mensalidade)}${cliFocus.status ? ` · status ${cliFocus.status}` : ''}`
          : undefined}
        open={!!cliFocus && !entryOpen}
        onClose={() => setSelected(null)}>
        {cliFocus && (
          <>
            <div className="metric-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <div className="metric"><div className="ml">Recebido</div>
                <div className="mv green">{fmt(cliFocus.recebido_total)}</div></div>
              <div className="metric"><div className="ml">Parcelas pagas</div>
                <div className="mv">{fmt(cliFocus.vencido ?? 0)}</div></div>
              <div className="metric"><div className="ml">A vencer</div>
                <div className="mv">{fmt(cliFocus.a_vencer ?? 0)}</div></div>
              <div className="metric"><div className="ml">Lucro (caixa)</div>
                <div className="mv" style={{ color: cliFocus.lucro >= 0 ? 'var(--ft-green)' : 'var(--bad)' }}>
                  {fmt(cliFocus.lucro)}</div></div>
              <div className="metric"><div className="ml">Compromisso total</div>
                <div className="mv">{fmt(cliFocus.investido + cliFocus.despesas)}</div></div>
              <div className="metric"><div className="ml">Time-to-even</div>
                <div className="mv" style={{ fontSize: 20 }}>
                  {cliFocus.ja_se_pagou ? 'se pagou ✓'
                    : cliFocus.payback_meses_restantes !== null
                      ? `~${cliFocus.payback_meses_restantes} meses` : '—'}
                </div></div>
            </div>

            <h4 style={{ margin: '18px 0 8px', fontSize: 11, letterSpacing: '.12em',
              textTransform: 'uppercase', color: 'var(--ap-muted)', fontFamily: 'JetBrains Mono, monospace' }}>
              Lançamentos desta quadra
            </h4>
            {detailEntries.length === 0 && (
              <div className="report-empty">Nenhum lançamento ainda.</div>
            )}
            {detailEntries.map((e) => (
              <div key={e.id} className="srow">
                <span className="sicon">
                  <Icon name={e.kind === 'investimento' ? 'chip' : e.kind === 'despesa' ? 'money' : e.kind === 'retirada' ? 'power' : 'check'} size={15} />
                </span>
                <div>
                  <div className="sname" style={{ fontSize: 13 }}>
                    {e.kind === 'investimento' ? 'Investimento' : e.kind === 'despesa' ? 'Despesa' : e.kind === 'retirada' ? 'Retirada de caixa' : 'Receita manual'}
                    {e.description ? ` — ${e.description}` : ''}
                  </div>
                  <div className="sdet">
                    {fmt(e.amount)}{e.installments > 1 ? ` em ${e.installments}x de ${fmt(e.amount / e.installments)}` : ''} · desde {e.startMonth}
                  </div>
                </div>
                <div className="sright" style={{ marginLeft: 'auto' }}>
                  <button className="iconbtn" title="Remover"
                    onClick={() => delMut.mutate(e.id)} disabled={delMut.isPending}>
                    <Icon name="trash" size={14} />
                  </button>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-green btn-sm" onClick={() => {
                setKind('investimento'); setCliente(cliFocus.nome); setDescription('');
                setAmount(''); setInstallments('1'); setStartMonth(data.mes_atual); setEntryOpen(true);
              }}>
                <Icon name="plus" size={13} /> Investimento / despesa
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => {
                setKind('receita_manual'); setCliente(cliFocus.nome); setDescription('');
                setAmount(''); setInstallments('1'); setStartMonth(data.mes_atual); setEntryOpen(true);
              }}>
                <Icon name="plus" size={13} /> Receita manual
              </button>
            </div>
          </>
        )}
      </Modal>

      <Modal title="Renomear conta do caixa"
        desc="Nome da conta onde cai a receita do Apertai (ex: Nubank, Inter)."
        open={contaOpen} onClose={() => setContaOpen(false)}
        confirmText="Salvar"
        onConfirm={async () => {
          try {
            await setFinanceConfig({ account_name: contaNome.trim() || 'Nubank' });
            invalidate();
            setContaOpen(false);
            toast('Conta renomeada');
          } catch (e: any) { toast(`Erro: ${e.message}`, 'warn'); }
        }}>
        <label>Nome da conta</label>
        <input value={contaNome} onChange={(e) => setContaNome(e.target.value)} placeholder="Nubank" />
      </Modal>

      <Modal title="Lançar no financeiro"
        desc="Gravado no seu banco (Firestore). Investimento conta para o payback da quadra; despesa entra no custo; receita manual serve para registrar meses antigos."
        open={entryOpen} onClose={() => setEntryOpen(false)}
        onConfirm={() => addMut.mutate({
          kind, cliente: cliente.trim() || null as any, description: description.trim(),
          amount: Number(amount), installments: Number(installments) || 1,
          startMonth: startMonth || data.mes_atual,
        })}
        confirmText="Salvar" busy={addMut.isPending}>
        <label>Tipo</label>
        <select value={kind} onChange={(e) => setKind(e.target.value as any)}>
          <option value="investimento">Investimento (custo inicial da quadra)</option>
          <option value="despesa">Despesa</option>
          <option value="receita_manual">Receita manual (mês antigo)</option>
          <option value="retirada">Retirada de caixa (saque/transferência — só abate o caixa)</option>
        </select>
        <label style={{ marginTop: 12 }}>Quadra / cliente (igual à planilha; vazio = Apertai geral)</label>
        <input list="fin-clientes" value={cliente} onChange={(e) => setCliente(e.target.value)}
          placeholder="ex: Arena Prime" />
        <datalist id="fin-clientes">
          {data.clientes.map((c) => <option key={c.nome} value={c.nome} />)}
        </datalist>
        <label style={{ marginTop: 12 }}>Descrição</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="ex: implantação 2 câmeras" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 12 }}>
          <div>
            <label>Valor total (R$)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="2000" />
          </div>
          <div>
            <label>Parcelas</label>
            <input type="number" min={1} max={48} value={installments}
              onChange={(e) => setInstallments(e.target.value)} />
          </div>
          <div>
            <label>1ª parcela (AAAA-MM)</label>
            <input value={startMonth} onChange={(e) => setStartMonth(e.target.value)}
              placeholder={data.mes_atual} />
          </div>
        </div>
        {cliFocus && kind === 'investimento' && (
          <p style={{ marginTop: 12, fontSize: 12, color: 'var(--ap-muted)' }}>
            {cliFocus.nome} paga {fmt(cliFocus.mensalidade)}/mês → R$ {amount || '?'} se paga em
            ~{amount && cliFocus.mensalidade > 0 ? Math.ceil(Number(amount) / cliFocus.mensalidade) : '?'} meses.
          </p>
        )}
      </Modal>
    </>
  );
}
